import type { Entries } from '@eformulieren/model'
import type { LoginOption } from '@eformulieren/renderer'

/**
 * Uitloggen en verlengen bij de inlogdienst (R-154, R-139, WCAG 2.2.1, ontwerp 3.3).
 *
 * De beslissingen staan hier `window`-vrij, zodat ze in Node te toetsen zijn (dezelfde keuze als
 * `login.ts`); alleen `logOut` doet een verzoek.
 */

/**
 * De ingang van wie hier werkt: het deel van de persoonsaanduiding vóór de dubbele punt.
 *
 * `<gastheer>:<vaste sleutel>` (R-155e, `apps/runtime/src/token.ts`), en de gastheer is een slug, dus
 * de eerste dubbele punt scheidt. Komt de gastheer niet voor bij de ingangen van de pod, dan niets.
 */
export function entryOf(entries: Entries, persoon: string): Entries['ingangen'][number] | undefined {
  const separator = persoon.indexOf(':')
  if (separator <= 0) return undefined
  const gastheer = persoon.slice(0, separator)
  return entries.ingangen.find((entry) => entry.gastheer === gastheer)
}

/**
 * De knop "Sessie verlengen": `<inlogdienst>/<gastheer>/verlengen?doel=`, bij de ingang van wie hier
 * werkt.
 *
 * **Niet `starten`.** Met een lopende sessie geeft `starten` een token met dezelfde deadline, en dan
 * verlengt de knop niets; `verlengen` gaat altijd langs de identiteitsprovider
 * (`apps/inlog/src/server.ts`). Is de ingang van de persoon niet bekend, dan de ingangen aan de eigen
 * kant, zoals bij opnieuw inloggen (`reloginOptions`).
 */
export function renewOptions(
  entries: Entries | undefined,
  persoon: string | undefined,
  kant: 'intern' | 'extern' | undefined,
  target: string,
): readonly LoginOption[] {
  if (entries === undefined) return []
  const address = (gastheer: string): string => {
    const url = new URL(`${entries.inlogdienst}/${encodeURIComponent(gastheer)}/verlengen`)
    url.searchParams.set('doel', target)
    return url.href
  }
  const own = persoon === undefined ? undefined : entryOf(entries, persoon)
  if (own !== undefined) return [{ gastheer: own.gastheer, kant: own.kant, adres: address(own.gastheer) }]
  const all = entries.ingangen.map((entry) => ({ gastheer: entry.gastheer, kant: entry.kant, adres: address(entry.gastheer) }))
  const ownSide = kant === undefined ? [] : all.filter((option) => option.kant === kant)
  return ownSide.length > 0 ? ownSide : all
}

/** Waar het uitloggen heen gaat, of niets als de ingang van de persoon niet bekend is. */
export function logoutAddress(entries: Entries, persoon: string): string | undefined {
  const own = entryOf(entries, persoon)
  return own === undefined ? undefined : `${entries.inlogdienst}/${encodeURIComponent(own.gastheer)}/uitloggen`
}

/**
 * Uitloggen: een `fetch` in de CORS-modus met de cookies, en dan naar de pagina na het uitloggen.
 *
 * **Geen form-post.** De pagina zet `Referrer-Policy: no-referrer`, en dan stuurt een browser bij een
 * form-post naar een andere oorsprong `Origin: null`; de inlogdienst weigert een uitlogverzoek zonder
 * herkomst uit zijn doellijst. In de CORS-modus is `Origin` altijd de echte. Het doel gaat mee, zodat de
 * pagina na het uitloggen "Opnieuw inloggen" naar dit formulier kan laten wijzen.
 *
 * Geeft het adres van die pagina terug, of werpt bij elke tegenslag: de knop toont dan een melding.
 */
export async function logOut(address: string, target: string, send: typeof fetch = fetch): Promise<string> {
  const response = await send(address, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: { 'content-type': 'application/x-www-form-urlencoded', accept: 'application/json' },
    body: new URLSearchParams({ doel: target }).toString(),
  })
  if (!response.ok) throw new Error(`uitloggen gaf ${String(response.status)}`)
  const body = (await response.json()) as { naar?: unknown }
  if (typeof body.naar !== 'string' || !body.naar.startsWith(`${new URL(address).origin}/`)) {
    throw new Error('uitloggen gaf geen adres bij de inlogdienst terug')
  }
  return body.naar
}
